import React, { useState, useEffect, useRef } from "react";
import { QuestionHead, QuestionHeader } from "../styled/styles";
import styled from "styled-components";
import ErrorDisplay from "./ErrorDisplay";

const StyledDiv = styled.div`
  padding: 0 15px;
  display: ${(props) => (props.toDisplay ? "block" : "none")};
  margin-bottom: 1.5rem;
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: ${props => props.hasError ? '1.5rem' : '0'};
  border-radius: ${props => props.hasError ? '0.5rem' : '0'};
  border: ${props => props.hasError ? `2px solid ${props.theme.errorBorder}` : 'none'};
  background-color: ${props => props.hasError ? props.theme.errorBg : 'transparent'};
`;

const ModernQuestionHead = styled(QuestionHead)`
  font-size: 1.125rem;
  font-weight: 500;
  color: ${props => props.theme.textSecondary};
  margin-top: 0;
  margin-bottom: 0;
`;

const ModernQuestionHeader = styled(QuestionHeader)`
  // font-size: 1.5rem;
  // font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin-top: 0;
  margin-bottom: 0;
  
  @media (min-width: 768px) {
    font-size: 1.875rem;
  }
`;

const AnswerOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 100%;
`;

const Header = styled.div`
  width: 100%;
  box-sizing: border-box;
  background-color: ${(props) => (props.selected ? props.theme.selectedBg : props.theme.cardBg)};
  color: ${props => props.theme.textPrimary};
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 2px solid ${(props) =>
    props.selected ? props.theme.selectedBorder : props.theme.borderPrimary};
  cursor: pointer;
  transition: ${props => props.theme.transition};
  
  &:hover {
    border-color: ${props => props.theme.selectedBorder};
  }
`;

const Arrow = styled.span`
  width: 10px;
  height: 10px;
  margin-right: 10px;
  border-right: 2px solid ${props => props.theme.selectedBorder};
  border-bottom: 2px solid ${props => props.theme.selectedBorder};
  transform: ${(props) =>
    props.expanded ? "rotate(-135deg)" : "rotate(45deg)"};
  transition: transform 0.3s ease;
  flex-shrink: 0;
`;

const AnswerCard = styled.button`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background-color: ${(props) => (props.selected ? props.theme.selectedBg : props.theme.cardBg)};
  color: ${props => props.theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 2px solid ${(props) =>
    props.selected ? props.theme.selectedBorder : props.theme.borderPrimary};
  cursor: pointer;
  text-align: left;
  transition: ${props => props.theme.transition};
  font-family: inherit;
  font-size: 1rem;
  
  &:hover {
    border-color: ${props => props.selected ? props.theme.selectedBorder : props.theme.borderSecondary};
  }
  
  input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  label {
    flex: 1;
    cursor: pointer;
    font-weight: ${props => props.selected ? '600' : '400'};
  }
`;

const CheckmarkIcon = styled.svg`
  width: 24px;
  height: 24px;
  color: ${props => props.theme.selectedBorder};
  flex-shrink: 0;
`;

const CollapsibleAnswerCard = styled(AnswerCard)`
  width: ${(props) => (props.isVisible ? "calc(100% - 1.5rem)" : "0")};
  height: ${(props) => (props.isVisible ? "auto" : "0")};
  margin: ${(props) => (props.isVisible ? "0 0 0 1.5rem" : "0")};
  padding: ${(props) => (props.isVisible ? "1rem" : "0")};
  overflow: hidden;
  display: ${(props) => (props.$hidden ? "none" : "flex")};
  align-items: flex-start;
  transition: height 0.3s ease, width 0.3s ease, padding 0.3s ease, margin 0.3s ease;
`;

const TextInput = styled.input`
  display: ${(props) => (props.show ? "block" : "none")};
  width: 100%;
  box-sizing: border-box;
  margin-top: 0.5rem;
  padding: 0.5rem;
  border: none;
  border-top: 1px solid ${props => props.theme.borderPrimary};
  background-color: inherit;
  color: inherit;
  font-size: inherit;
  font-family: inherit;
  
  &:focus {
    outline: none;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FineprintParagraph = styled.p`
  font-size: 0.75rem;
  font-family: inherit;
  font-style: italic;
  font-weight: normal;
  line-height: 1.42857143;
  color: ${props => props.theme.textMuted};
  margin-top: 0.5rem;
`;

const Fineprint = ({ text }) => {
  if (text) {
    let beforeLink = text.split("https://")[0];
    let link = text.split("https://")[1];
    return (
      <FineprintParagraph>
        {beforeLink}{" "}
        <a
          style={{ color: "#0071C5", textDecoration: "none" }}
          href={`https://${link}`}
          target="_blank"
        >{`https://${link}`}</a>
      </FineprintParagraph>
    );
  }
};

const Single = ({
  currentPage,
  errors,
  setErrors,
  toDisplay,
  question,
  answeredQuestions,
  setAnsweredQuestions,
  currentPageAnswers,
  setCurrentPageAnswers,
  setOtherErrors,
  showQuestion,
  parseDynamicQuestionText,
  questionsOnPage,
  shouldAutoAdvance,
  onAutoAdvance
}) => {
  const [selectedState, setSelectedState] = useState(question.Answers.map(answer => answer.Expression).some(ans => ans.includes('_o')) && answeredQuestions[currentPage - 1]?.find((answer) =>
    answer.includes("_o")) ? question.Answers.map(answer => answer.Expression).find(ans => ans.includes('_o')) : answeredQuestions[currentPage - 1]?.length ? answeredQuestions[currentPage - 1][0]  : '');
  const [refArray, setRefArray] = useState();
  const [pendingAutoAdvance, setPendingAutoAdvance] = useState(false);
   
   
               
  const [otherText, setOtherText] = useState(
    question.Answers.map(answer => answer.Expression).some(ans => ans.includes('_o')) && answeredQuestions[currentPage - 1]?.find((answer) =>
      answer.includes("_o")) 
      ? (answeredQuestions[currentPage - 1]?.find((answer) =>
          answer.includes("_o")
        ).split('=')[1] !== "null" && answeredQuestions[currentPage - 1]?.find((answer) =>
          answer.includes("_o")
        ).split('=')[1] !== "-998") ? answeredQuestions[currentPage - 1]?.find((answer) =>
          answer.includes("_o")
        ).split('=')[1]
      : "" : ""
  );
  const textInputRef = useRef(null);
  const otherInputRef = useRef(null);   

  useEffect(() => {
     
    if (currentPageAnswers?.length || answeredQuestions[currentPage - 1]?.length) {
      let answeredQuestionsCopy = [...answeredQuestions];
      let answeredPagesLength = answeredQuestionsCopy.length; 
      let currentPageIndex = currentPage - 1; 

      if (answeredPagesLength - currentPageIndex !== 1) {
        let difference = 1 - (answeredPagesLength - currentPageIndex)
        for (let i = 0; i < difference; i++) {
          answeredQuestionsCopy.push([])
        }
        currentPageAnswers = []
      }
      //currentPageAnswers.filter
      answeredQuestionsCopy[currentPage - 1] = answeredQuestionsCopy[currentPage - 1].filter(ans => !ans.includes('o=null'));
           
      setAnsweredQuestions(answeredQuestionsCopy);
              
      setOtherText(question.Answers.map(answer => answer.Expression).some(ans => ans.includes('_o')) && answeredQuestions[currentPage - 1]?.find((answer) =>
        answer.includes("_o")) 
        ? (answeredQuestions[currentPage - 1]?.find((answer) =>
            answer.includes("_o")
          ).split('=')[1] !== "null" && answeredQuestions[currentPage - 1]?.find((answer) =>
            answer.includes("_o")
          ).split('=')[1] !== "-998") ? 
          answeredQuestions[currentPage - 1]?.find((answer) =>
            answer.includes("_o")
          ).split('=')[1] : ""
        : "")
    } else if (answeredQuestions[currentPage - 1]?.length) {
      let answeredQuestionsCopy = [...answeredQuestions];
      answeredQuestionsCopy[currentPage - 1] = answeredQuestions[currentPage - 1].filter(ans => !ans.includes('o=null'));
           
      setAnsweredQuestions(answeredQuestionsCopy);
                 
      setOtherText(question.Answers.map(answer => answer.Expression).some(ans => ans.includes('_o')) && answeredQuestions[currentPage - 1]?.find((answer) =>
        answer.includes("_o")) 
        ? answeredQuestions[currentPage - 1]?.find((answer) =>
            answer.includes("_o")
          ).split('=')[1]
        : "") 
      
    } else {
              
      setOtherText("");
    }
  }, []);

  useEffect(() => {
     
    if (currentPageAnswers && !currentPageAnswers.some(ans => ans.includes('_o')) && answeredQuestions[currentPage - 1] && !answeredQuestions[currentPage - 1].some(ans => ans.includes('_o'))) {
                 
      setOtherText("");
    } else {
         
        if (textInputRef.current) {
                         
          setOtherText(question.Answers.map(answer => answer.Expression).some(ans => ans.includes('_o')) && answeredQuestions[currentPage - 1]?.find((answer) =>
            answer.includes("_o")) 
            ? ((answeredQuestions[currentPage - 1]?.find((answer) =>
                answer.includes("_o")
              ).split('=')[1] !== "" && answeredQuestions[currentPage - 1]?.find((answer) =>
                answer.includes("_o")
              ).split('=')[1] !== "null") && (answeredQuestions[currentPage - 1]?.find((answer) =>
                answer.includes("_o")
              ).split('=')[1] !== "" && answeredQuestions[currentPage - 1]?.find((answer) =>
                answer.includes("_o")
              ).split('=')[1] !== "-998"))  ? answeredQuestions[currentPage - 1]?.find((answer) =>
                answer.includes("_o")
              ).split('=')[1] : "" 
            : "")
          
          textInputRef.current.value = answeredQuestions[currentPage - 1][0].split('=')[1]
         
                
          textInputRef.current.focus();
        }
      
    }
  },[currentPage])

  useEffect(() => {
       
    setTimeout(() => {
       
      if (textInputRef.current && answeredQuestions[currentPage - 1] && answeredQuestions[currentPage - 1][0]?.includes('_o_')) {
        document.querySelector('[value*="_o="]').click();
              
        textInputRef.current.focus()
           
        textInputRef.current.value = (answeredQuestions[currentPage - 1][0].split('=')[1] !== "null" && answeredQuestions[currentPage - 1][0].split('=')[1] !== "-998") ? answeredQuestions[currentPage - 1][0].split('=')[1] : "" 
                 
        setOtherText((answeredQuestions[currentPage - 1][0].split('=')[1] !== "null" && answeredQuestions[currentPage - 1][0].split('=')[1] !== "-998") ? answeredQuestions[currentPage - 1][0].split('=')[1] : "")
      } else if (textInputRef.current && currentPageAnswers && currentPageAnswers?.includes('_o_')) {
        document.querySelector('[value*="_o="]').click();
              
        textInputRef.current.focus()
           
        textInputRef.current.value = answeredQuestions[currentPage - 1][0].split('=')[1]
                 
        setOtherText(currentPageAnswers[0].split('=')[1])
      }
    }, 100)

  },[`${textInputRef?.current?.classList?.value}`])

  useEffect(() => {
      
    let expandedHeaders = currentPageAnswers?.map(ansExpression => question.Answers.find(ansObj => ansObj.Expression === ansExpression)?.GroupName).filter(ans => ans)
   
    setIsHeaderExpanded(question.Answers.map((response) => expandedHeaders?.includes(response.AnswerLabel)))
      
    setHideDisplay(question.Answers.map((response) => currentPageAnswers?.includes(response.Expression)));

    if (refArray && refArray.every((ref) => ref?.current?.id !== question.Answers[0].AnswerCode)) {
        ;
      refArray.forEach((refElement) => {
        if (refElement.current) {
          refElement.current.remove();
        }
      });
    }

    setRefArray(question.Answers.map(() => React.createRef(null)));
  }, [JSON.stringify(question.Answers)]);

  useEffect(() => {
         
    if (currentPageAnswers.length === 0) {
      setIsHeaderExpanded(question.Answers.map((response) => false));
      setHideDisplay(question.Answers.map((response) => true));
    } else {
      let expandedHeaders = currentPageAnswers?.map(ansExpression => question.Answers.find(ansObj => ansObj.Expression === ansExpression)?.GroupName).filter(ans => ans)
     
      setIsHeaderExpanded(question.Answers.map((response) => expandedHeaders?.includes(response.AnswerLabel)))
        
      setHideDisplay(question.Answers.map((response) => currentPageAnswers?.includes(response.Expression)));
    }
  }, [currentPage])

  useEffect(() => {
     
    if (
      textInputRef.current &&
      answeredQuestions[currentPage - 1]?.includes(
        textInputRef.current.dataset.code
      )
    ) {
      //    
            
      textInputRef.current.focus();
    }
  }, [answeredQuestions]);

  useEffect(() => {
    let emailAnswer = question.Answers.find(
      (answer) => answer.AnswerType === "Email"
    );
    if (emailAnswer) {
      let emailAnswerExpression = emailAnswer.Expression;
      if (
        currentPageAnswers?.find((pageAnswer) =>
          pageAnswer.includes(emailAnswerExpression)
        )
      ) {
        let answeredQuestionsCopy = [...answeredQuestions];
        answeredQuestionsCopy[currentPage - 1] = [
          ...currentPageAnswers.filter(
            (answer) => !answer.includes(emailAnswerExpression.split("=")[0])
          ),
          `${emailAnswerExpression.split("=")[0]}_${
            emailAnswerExpression.split("=")[1]
          }=`,
        ];
             
        setAnsweredQuestions([...answeredQuestionsCopy]);

        setCurrentPageAnswers([
          ...currentPageAnswers.filter(
            (answer) => !answer.includes(emailAnswerExpression.split("=")[0])
          ),
          `${emailAnswerExpression.split("=")[0]}_${
            emailAnswerExpression.split("=")[1]
          }=`,
        ]);
      }
    }
  }, [JSON.stringify(currentPageAnswers)]);

  useEffect(() => {
    let previousSelectedAnswer = currentPageAnswers?.find((answer) =>
      answer.includes(question.Answers[0].AnswerCode)
    );
    if (!toDisplay) {
      let answeredQuestionsCopy = [...answeredQuestions];

      setSelectedState("");

      //if the answer has been previously selected
      if (previousSelectedAnswer) {
        answeredQuestionsCopy[currentPage - 1] = [
          ...answeredQuestionsCopy[currentPage - 1]?.filter(
            (answers) => !answers.includes(question.Answers[0].AnswerCode)
          ),
          `${previousSelectedAnswer.split("=")[0]}=null`,
        ];
      } else {
        answeredQuestionsCopy[currentPage - 1] = answeredQuestionsCopy[
          currentPage - 1
        ].filter(
          (answers) => !answers.includes(question.Answers[0].AnswerCode)
        );
      }
           
      setAnsweredQuestions(answeredQuestionsCopy);

      setCurrentPageAnswers(answeredQuestionsCopy[currentPage - 1]);
    } else {
      if (previousSelectedAnswer && previousSelectedAnswer.includes("null")) {
        let answeredQuestionsCopy = [...answeredQuestions];
        answeredQuestionsCopy[currentPage - 1] = [
          ...answeredQuestionsCopy[currentPage - 1]?.filter(
            (answers) => !answers.includes(question.Answers[0].AnswerCode)
          ),
        ];
             
        setAnsweredQuestions(answeredQuestionsCopy);

        setCurrentPageAnswers(answeredQuestionsCopy[currentPage - 1]);
      }
    }
  }, [toDisplay]);

  // Auto-advance effect: triggers when answers are updated
  useEffect(() => {
    // Only proceed if auto-advance is pending
    if (!pendingAutoAdvance) return;
    
    // Check if we should auto-advance
    if (shouldAutoAdvance && onAutoAdvance) {
      const pageIndex = currentPage - 1;
      const pageAnswers = answeredQuestions[pageIndex];
      
      // Check if this question has been answered (answer exists in state)
      if (pageAnswers && pageAnswers.length > 0) {
        const firstAnswerCode = question.Answers[0]?.AnswerCode?.split('_')[0];
        const hasAnswer = pageAnswers.some(
          (answer) => typeof answer === 'string' && answer.includes(`${firstAnswerCode}`)
        );
        
        // If answer exists and doesn't require text input, auto-advance
        if (hasAnswer) {
          const selectedAnswer = pageAnswers.find(
            (answer) => typeof answer === 'string' && answer.includes(`${firstAnswerCode}`)
          );
          
          // Don't auto-advance if answer requires text input (contains _o)
          if (selectedAnswer && !selectedAnswer.includes('_o=')) {
            setTimeout(() => {
              setPendingAutoAdvance(false);
              onAutoAdvance();
            }, 400);
          } else {
            setPendingAutoAdvance(false);
          }
        }
      }
    }
  }, [answeredQuestions, currentPage, pendingAutoAdvance, shouldAutoAdvance, onAutoAdvance, question.Answers]);

  const handleRadioChange = (event, answerExpression) => {
         
    setSelectedState(answerExpression);

    let answeredQuestionsCopy = [...answeredQuestions];
     
    let prevAnswer = null;


      let answeredPagesLength = answeredQuestionsCopy.length; 
      let currentPageIndex = currentPage - 1; 

      if (answeredPagesLength - currentPageIndex !== 1) {
        let difference = 1 - (answeredPagesLength - currentPageIndex)
        for (let i = 0; i < difference; i++) {
          answeredQuestionsCopy.push([])
        }
        // currentPageAnswers = []
      }   
       
    answeredQuestionsCopy[currentPage - 1] = !answeredQuestionsCopy[currentPage - 1]?.filter(answer => typeof answer === 'string').map(answer => answer.split('=')[0]).includes(answerExpression.split('=')[0])
      ? [
          ...answeredQuestionsCopy[currentPage - 1],
          answerExpression,
        ]
      : [...answeredQuestionsCopy[currentPage - 1].filter(answer => typeof answer === 'object' || answer.split('=')[0] !== answerExpression.split('=')[0]), ...answerExpression.includes('_o') && otherText ? [`${answerExpression.split('=')[0]}_${answerExpression.split('=')[1]}=${otherText}`] : !answerExpression.includes('_o') ? [answerExpression, ...question.Answers.filter(ansObj => (ansObj.AnswerType === 'Email' || ansObj.AnswerType === 'Other' || ansObj.AnswerType === 'Numeric' || ansObj.AnswerType === 'FloatingPoint')).map(ansObj => `${ansObj.Expression.split('=')[0]}=-998`)] : [answerExpression]];

              
    setAnsweredQuestions(answeredQuestionsCopy);
    setCurrentPageAnswers(answeredQuestionsCopy[currentPage - 1]);

    if (textInputRef.current && !answerExpression.includes('_o')) {
                
      textInputRef.current.value = "";
    }

    setOtherErrors([]);
    setErrors(
      errors.filter(
        (errorExpression) =>
          errorExpression !== event.currentTarget?.firstChild?.dataset?.code
      )
    );
    
    // Set flag to trigger auto-advance after state updates
    if (shouldAutoAdvance && !answerExpression.includes('_o')) {
      setPendingAutoAdvance(true);
    }
  };

  const handleInputChange = (event, answerExpression, type) => {

    let newValue =
      event.target.dataset.code.includes("o") && type === "Email"
        ? `${answerExpression.split("=")[0]}=${event.target.value}`
        : `${answerExpression.split("=")[0]}_${
            answerExpression.split("=")[1]
          }=${event.target.value}`;

    let answeredQuestionsCopy = [...answeredQuestions];

    if (
      type === "Email" ||
      type === "Other" ||
      type === "Numeric" ||
      type === "FloatingPoint"
    ) {
      answeredQuestionsCopy[currentPage - 1] = answeredQuestionsCopy[
        currentPage - 1
      ].filter(
        (answerExpressions) =>
          !answerExpressions.includes(
            `${answerExpression.split("=")[0]}_${
              answerExpression.split("=")[1]
            }`
          ) && answerExpressions !== answerExpression
      );
    }

    answeredQuestionsCopy[currentPage - 1] = [
      ...answeredQuestionsCopy[currentPage - 1]?.filter(
        (answer) => !answer.includes(newValue.split("=")[0])
      ),
      newValue,
    ];

         
    setAnsweredQuestions(answeredQuestionsCopy);

    setCurrentPageAnswers([
      ...currentPageAnswers.filter(
        (answer) => !answer.includes(newValue.split("=")[0])
      ),
      newValue,
    ]);
             
    setOtherText(newValue.split("=")[1]);

    setErrors(
      errors.filter(
        (errorExpression) =>
          errorExpression !== event.target?.dataset.questioncode
      )
    );
  };

  const blahblah = (currentPageAnswers, answer) => {
      let currentPageAns = answeredQuestions[currentPage - 1]
      let answeredQuestionsCopy
    
      if (currentPageAns) {
        answeredQuestionsCopy = [...currentPageAns]
      } else {
        answeredQuestionsCopy = []
      }
      answeredQuestionsCopy = answeredQuestionsCopy.filter(ans => !ans.includes('o=null') && !ans.includes('=-998'))
  
      return !!(answeredQuestionsCopy?.length
        ? answeredQuestionsCopy.includes(answer.Expression) ||
            answeredQuestionsCopy?.find((pageAnswer) =>
              (pageAnswer.includes("_") && !pageAnswer.includes("o"))
                ? (`${pageAnswer?.split("_")[0]}=${pageAnswer?.split("_")[1]}`
                    .slice(0, -1)
                    .includes(answer.Expression) ||
                  pageAnswer.includes(answer.Expression))
                : pageAnswer.includes("o") ? 
                (answeredQuestionsCopy?.find((pageAnswer) =>
                  pageAnswer.includes("_o")
                ) &&
                  (answer.AnswerType === "Other" || answer.AnswerType === "Numeric" || answer.AnswerType === "Email" || answer.AnswerType === "FloatingPoint")) : 
                pageAnswer === answer.Expression
            ) 
        : selectedState === answer.Expression);
  };

  const [isHeaderExpanded, setIsHeaderExpanded] = useState();

  const [hideDisplay, setHideDisplay] = useState();

  const [currentHeaderIndexClicked, setCurrentHeaderIndexClicked] = useState();

  const renderBody = (answers) => {
    return answers.filter(answer => showQuestion(answer.showIf)).map((answer, i) => {
      window.shown ? window.shown++ : window.shown = 1;
      const isSelected = blahblah(currentPageAnswers, answer);
      
      return (answer.GroupName === "header" ? (
        <Header
          id={answer.AnswerCode}
          key={answer.AnswerCode}
          onClick={() => handleHeaderClick(i)}
          selected={!!isHeaderExpanded ? isHeaderExpanded[i] : false}
          ref={refArray[i]}
        >
          <Arrow expanded={!!isHeaderExpanded ? isHeaderExpanded[i] : false} />
          {answer.AnswerLabel}
        </Header>
      ) : answer.GroupName ? (
        <CollapsibleAnswerCard
          as="div"
          key={answer.Expression}
          isVisible={
            !!isHeaderExpanded
              ? isHeaderExpanded[
                  question.Answers.findIndex(
                    (answerObj) => answerObj.AnswerLabel === answer.GroupName
                  )
                ]
              : false
          }
          $hidden={
            !!hideDisplay
              ? isHeaderExpanded[
                  question.Answers.findIndex(
                    (answerObj) => answerObj.AnswerLabel === answer.GroupName
                  )
                ]
                ? hideDisplay[
                    question.Answers.findIndex(
                      (answerObj) => answerObj.AnswerLabel === answer.GroupName
                    )
                  ]
                : true
              : true
          }
          onClick={(e) => {
            handleRadioChange(e, answer.Expression);
          }}
          selected={
            currentPageAnswers.length
              ? currentPageAnswers.includes(answer.Expression) ||
                currentPageAnswers.some(
                  (pageAnswer) =>
                    `${pageAnswer?.split("_")[0]}=${pageAnswer?.split("_")[1]}`
                      .slice(0, -1)
                      .includes(answer.Expression) ||
                    pageAnswer.includes(answer.Expression)
                )
              : selectedState === answer.Expression
          }
        >
          <input
            type="radio"
            id={answer.AnswerId}
            name={`answer-${question.QuestionNo}`}
            data-code={question.QuestionCode}
            value={answer.Expression}
            checked={blahblah(currentPageAnswers, answer)}
            onChange={(e) => {
              handleRadioChange(e, answer.Expression);
            }}
          />
          <label htmlFor={answer.AnswerCode} dangerouslySetInnerHTML={{ __html: parseDynamicQuestionText(answer.AnswerLabel, true)}}></label>
          {isSelected && (
            <CheckmarkIcon viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
            </CheckmarkIcon>
          )}
          {(answer.AnswerType === "Email" ||
            answer.AnswerType === "Other" ||
            answer.AnswerType === "Numeric") && (
            <TextInput
              type="text"
              data-code={answer.Expression}
              data-questioncode={question.QuestionCode}
              ref={textInputRef}
              disabled={selectedState !== answer.Expression}
              show={
                currentPageAnswers.length
                  ? answer.AnswerType === "Email"
                    ? currentPageAnswers?.find(
                        (pageAnswer) =>
                          `${pageAnswer?.split("_")[0]}=${
                            pageAnswer?.split("_")[1]
                          }`
                            .slice(0, -1)
                            .includes(answer.Expression) ||
                          pageAnswer.includes(answer.Expression)
                      )
                    : selectedState === answer.Expression
                  : false
              }
              value={
                answer.AnswerType === "Email"
                  ? currentPageAnswers
                      .find((answered) =>
                        answered.includes(answer.Expression?.split("=")[0])
                      )
                      ?.split("=")[1]
                  : otherText
              }
              onChange={(e) => {
                handleInputChange(e, answer.Expression, answer.AnswerType);
              }}
            />
          )}
        </CollapsibleAnswerCard>
      ) : (
        <AnswerCard
          onClick={(e) => {
            handleRadioChange(e, answer.Expression);
          }}
          selected={isSelected}
          key={answer.Expression}
        >
          <input
            type="radio"
            id={answer.AnswerId}
            name={`answer-${question.QuestionNo}`}
            data-code={question.QuestionCode}
            value={answer.Expression}
            checked={isSelected}
            onChange={(e) => {
              handleRadioChange(e, answer.Expression);
            }}
          />
          <label htmlFor={answer.AnswerCode} dangerouslySetInnerHTML={{ __html: parseDynamicQuestionText(answer.AnswerLabel, true)}}></label>
          {isSelected && (
            <CheckmarkIcon viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
            </CheckmarkIcon>
          )}
          {(answer.AnswerType === "Email" ||
            answer.AnswerType === "Other" ||
            answer.AnswerType === "Numeric") && (
            <TextInput
              type="text"
              data-code={answer.Expression}
              data-questioncode={question.QuestionCode}
              ref={textInputRef}
              disabled={selectedState !== answer.Expression}
              show={
                currentPageAnswers.length
                ? answer.AnswerType === "Email"
                  ? currentPageAnswers?.find(
                      (pageAnswer) => 
                          typeof pageAnswer === 'string' ?
                        (`${pageAnswer?.split("_")[0]}=${
                          pageAnswer?.split("_")[1]
                        }`
                          .slice(0, -1)
                          .includes(answer.Expression) ||
                        pageAnswer.includes(answer.Expression)) :
                        pageAnswer.length ?
                          (`${pageAnswer[0]?.split("_")[0]}=${
                          pageAnswer[0]?.split("_")[1]
                        }`
                          .slice(0, -1)
                          .includes(answer.Expression) ||
                        pageAnswer[0].includes(answer.Expression))
                        : false
                    )
                  : selectedState === answer.Expression
                : false
              }
              value={
                answer.AnswerType === "Email"
                  ? currentPageAnswers
                      .find((answered) =>
                        answered.includes(answer.Expression?.split("=")[0])
                      )
                      ?.split("=")[1]
                  : otherText
              }
              onChange={(e) => {
                handleInputChange(e, answer.Expression, answer.AnswerType);
              }}
            />
          )}
        </AnswerCard>
      )
      )
    }
    );
  };

  const handleHeaderClick = (i) => {
    setCurrentHeaderIndexClicked(i);
    if (hideDisplay[i]) {
      let hideDisplayCopy = hideDisplay;
      hideDisplayCopy[i] = !hideDisplay[i];
      setHideDisplay([...hideDisplayCopy]);
    } else {
      let isHeaderExpandedCopy = isHeaderExpanded;
      isHeaderExpandedCopy[i] = !isHeaderExpanded[i];
      setIsHeaderExpanded([...isHeaderExpandedCopy]);
    }
  };

  useEffect(() => {
    if (currentHeaderIndexClicked) {
      if (!hideDisplay[currentHeaderIndexClicked]) {
        setTimeout(() => {
          let isHeaderExpandedCopy = isHeaderExpanded;
          isHeaderExpandedCopy[currentHeaderIndexClicked] = true;
          setIsHeaderExpanded([...isHeaderExpandedCopy]);
        }, 50);
      }
    }
  }, [JSON.stringify(hideDisplay)]);

  useEffect(() => {
    if (currentHeaderIndexClicked) {
      if (!isHeaderExpanded[currentHeaderIndexClicked]) {
        setTimeout(() => {
          let hideDisplayCopy = hideDisplay;
          hideDisplayCopy[currentHeaderIndexClicked] = true;
          setHideDisplay([...hideDisplayCopy]);
        }, 50);
      }
    }
  }, [JSON.stringify(isHeaderExpanded)]);
  // Check if there are errors for this question
  const hasError = errors && errors.includes(question.QuestionCode);

  return (
    <StyledDiv toDisplay={toDisplay}>
      <QuestionContainer hasError={hasError}>
        {question.QuestionHead && (
          <ModernQuestionHead dangerouslySetInnerHTML={{ __html: parseDynamicQuestionText(question.QuestionHead, true) }} />
        )}
        <ModernQuestionHeader dangerouslySetInnerHTML={{ __html: parseDynamicQuestionText(question.QuestionName, true) }} errors={errors} question={question}>
        </ModernQuestionHeader>
        <AnswerOptionsContainer>
          {refArray && renderBody(question.Answers)}
        </AnswerOptionsContainer>
        {question.QuestionNote && <Fineprint text={question.QuestionNote} />}
        
        <ErrorDisplay errors={errors} questionCode={question.QuestionCode} />
      </QuestionContainer>
    </StyledDiv>
  );
};

export default Single;
